/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from "react";
import {
    Category,
    Content,
    ContentDetails,
    ContentResponse,
    ContentType,
    ReceivedGift,
    ResumeContent,
} from "JS/Models";
import { createOfflineIDBID, sortArrayByKey } from "JS/Helpers";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import {
    setGlobalVideos,
    setGlobalPlayedVideos,
    setGlobalUserResumeVideos,
    setVideoDetails,
} from "JS/Redux/Video";
import { VideoService } from "JS/Services/Video";
import moment from "moment";
import { config } from "JS/Config";
import { useBulkUpdateVideosDB, useVideoIndexFromDb } from "./Database/Video";
import { VideoDexie } from "JS/Database/Dexie";
import { keyBy } from "lodash";
import {
    AudioVideoResume,
    PlayedAudiosVideos,
} from "JS/Models/Firebase/Resume";
import { useGlobalMediaEssentials } from "./MediaEssentials";
import { messaging } from "JS/Helpers/UserMessaging";
import { useContentDetails } from "./Media";
import { useContentUpdate } from "./ContentUpdate";
import { setVideoSpeakerFetchStatus } from "JS/Redux/Attributes";
import { setRetVideoContentUpdateRequired } from "JS/Redux/RetiredContent";

export const service = new VideoService();

export const useVideos = () => {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [response, setResponse] = useState<ContentResponse>(null);
    const { resumeVideos, playedVideos, cacheTime, videos } = useGlobalVideos();
    const [allVideos, setAllVideos] = useState<Content[]>(videos || []);

    const playedVideosList =
        playedVideos && playedVideos[config.user.memberId]
            ? playedVideos[config.user.memberId]
            : [];

    const dispatch = useAppDispatch();
    const { syncOfflineVideos } = useSyncOfflineVideos();
    const { mediaEssentials } = useGlobalMediaEssentials();
    const { enqueueSnackbar } = useSnackbar();
    const { fetch: fetchContentUpdate } = useContentUpdate();

    const refetch = useCallback(() => {
        setLoading(true);
        return service
            .getVideos()
            .then(async (response) => {
                const data = response?.data;
                setResponse(data);
                if (data) {
                    syncOfflineVideos(data?.content);
                    const allVideos: Content[] = data?.content?.map((item) => {
                        const playedVideos = playedVideosList.filter(
                            (x) => x.nid === item.nid,
                        );

                        return {
                            ...item,
                            played: playedVideos.length
                                ? playedVideos[0].played
                                : "0",
                            isReceived: false,
                        };
                    });

                    const categoryList: Category[] = data?.categories;

                    const videosList = sortArrayByKey(
                        allVideos,
                        "title",
                        "ASC",
                    );

                    setAllVideos(videosList);

                    dispatch(
                        setGlobalVideos({
                            videosList: videosList,
                            catagoriesList: sortArrayByKey(
                                categoryList,
                                "category_title",
                                "ASC",
                            ),
                            playedVideos: playedVideos
                                ? { ...playedVideos }
                                : {},
                            resumeVideos: resumeVideos
                                ? { ...resumeVideos }
                                : {},
                        }),
                    );

                    return {
                        allVideos,
                        categoryList,
                        status: response.status,
                    };
                }

                return {
                    allVideos: [],
                    categoryList: [],
                    status: false,
                };
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });

                return {
                    allVideos: [],
                    categoryList: [],
                    status: false,
                };
            })

            .finally(() => {
                setLoading(false);
                setLoaded(true);
            });
    }, []);

    useEffect(() => {
        fetchContentUpdate("Video", cacheTime).then((r) => {
            if (mediaEssentials?.allow_rule_engine_requests && r) {
                refetch();
                dispatch(setVideoSpeakerFetchStatus(false)); // was !r — always false under the && r guard
                dispatch(setRetVideoContentUpdateRequired(true)); // was r — always true under the && r guard
            }
        });
    }, []);

    return {
        refetch,
        loaded,
        allVideos,
        response,
        loading,
    };
};

export const useVideoDetails = (nid: string, contentType: ContentType) => {
    const { setVideoDetail } = useGlobalVideos();
    const { loading, detail, fetch } = useContentDetails(nid, contentType);

    useEffect(() => {
        if (!!detail) setVideoDetail(nid, detail);
    }, [detail]);

    useEffect(() => {
        fetch();
    }, []);

    return {
        loading,
    };
};

export const useGlobalVideos = () => {
    const dispatch = useAppDispatch();

    const allData = useAppSelector((state) => state.videos);
    const played = useAppSelector((state) => state.videos.playedVideos);
    const resume = useAppSelector((state) => state.videos.resumeVideos);

    const resumeUserVideos =
        resume && resume[`${config?.user?.memberId}`]
            ? resume[`${config?.user?.memberId}`]
            : [];

    const currentPlayedMap = played ? played : {};
    const resumeMap = resume ? resume : {};
    const playedUserVideos =
        played && played[`${config?.user?.memberId}`]
            ? played[`${config?.user?.memberId}`]
            : [];

    return {
        videos: allData?.videosList,
        cacheTime: allData?.cacheTime,
        resumeVideos: resume || {},
        resumeUserVideos: resumeUserVideos,

        categories: allData.catagoriesList,
        playedVideos: currentPlayedMap ? currentPlayedMap : {},
        playedUserVideos,
        videoDetails: allData?.videoDetails,

        setGlobalVideos: (videos: Content[]) => {
            dispatch(
                setGlobalVideos({
                    ...allData,
                    videosList: videos,
                }),
            );
        },
        setVideoDetail: (nid: string, detail: ContentDetails) => {
            const videoDetailsMap = !!allData?.videoDetails
                ? { ...allData.videoDetails }
                : {};
            videoDetailsMap[nid] = detail;
            dispatch(setVideoDetails(videoDetailsMap));
        },

        updateRecentlyPlayed: (video: Content | ReceivedGift) => {
            const currentPlayed: (
                | Content
                | ReceivedGift
                | PlayedAudiosVideos
            )[] = currentPlayedMap[config.user.memberId]
                ? currentPlayedMap[config.user.memberId]
                : [];
            const newMap = { ...currentPlayedMap };
            const newPlayed = [...currentPlayed].filter(
                (c) => c.nid !== video.nid,
            );
            newPlayed.push(video);
            newMap[config.user.memberId] = newPlayed;
            dispatch(setGlobalPlayedVideos(newMap));
        },
        updateResumeVideos: (resumeVideo: ResumeContent) => {
            const currentUserResume: ResumeContent[] = resumeMap[
                config.user.memberId
            ]
                ? resumeMap[config.user.memberId]
                : [];
            const newMap = { ...resumeMap };
            const newResume = [...currentUserResume].filter(
                (c) => c.nid !== resumeVideo.nid,
            );
            newResume.push(resumeVideo);
            newMap[config.user.memberId] = newResume;
            dispatch(setGlobalUserResumeVideos(newMap));
        },
        syncResumeVideos: (resumeAudios: AudioVideoResume[]) => {
            dispatch(
                setGlobalUserResumeVideos({
                    [config.user.memberId]: resumeAudios,
                }),
            );
        },
        syncPlayedVideos: (playedAudios: PlayedAudiosVideos[]) => {
            dispatch(
                setGlobalPlayedVideos({
                    [config.user.memberId]: playedAudios,
                }),
            );
        },
        getVideoDetails: (nid: string) => {
            const details = allData?.videoDetails;
            return !!details ? details[nid] : undefined;
        },
    };
};

export const useGlobalVideoDetails = (nid: string) => {
    const { videoDetails } = useGlobalVideos();

    return {
        videoDetails: !!videoDetails ? videoDetails[nid] : undefined,
    };
};

export const useSyncOfflineVideos = () => {
    const { refetch: refetchOfflineVideos } = useVideoIndexFromDb(true);
    const { bulkUpdateVideos } = useBulkUpdateVideosDB();

    const syncOfflineVideos = useCallback(
        async (videos: (Content | ReceivedGift)[]) => {
            const offlineVideos = await refetchOfflineVideos();

            if (offlineVideos && offlineVideos.length) {
                const allIds = offlineVideos.map((d) => d.id);

                const filteredVideos = videos.filter((x) =>
                    allIds.includes(
                        createOfflineIDBID(config.user.memberId, x.nid),
                    ),
                );

                const offlineVidMap = keyBy(offlineVideos, (x) => x.id);
                const newOfflineVideos: VideoDexie[] = filteredVideos.map(
                    (v) => {
                        const currVid =
                            offlineVidMap[
                                createOfflineIDBID(config.user.memberId, v.nid)
                            ];
                        return {
                            ...currVid,
                            name: v.title,
                        };
                    },
                );

                await bulkUpdateVideos(newOfflineVideos);
            }
        },
        [refetchOfflineVideos, bulkUpdateVideos],
    );

    return {
        syncOfflineVideos,
    };
};
